/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect, useCallback } from 'react'
import type {
  CustomBanner,
  PositionedBannerInput,
  PositionedBannerResponse,
} from 'api/generated'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import Select, { MultiValue, SingleValue } from 'react-select'

type BannerableType = PositionedBannerInput['bannerable_type']

interface BannerableTypeOption {
  value: BannerableType
  label: string
}

// ADSENSE is accepted by the API but not offered here (no AdSense slot picker)
const BANNERABLE_TYPES: BannerableTypeOption[] = [
  { value: 'TENNISTALKER', label: 'TENNISTALKER' },
]

interface BannerOption {
  value: string
  label: string
}

interface EditBannerFormProps {
  banner: PositionedBannerResponse | null | undefined
  onUpdate: (id: number, data: PositionedBannerInput) => void
  customBanners: CustomBanner[] | null | undefined
}

export const EditBannerForm: React.FC<EditBannerFormProps> = ({
  banner,
  onUpdate,
  customBanners,
}) => {
  const [id, setId] = useState<number | undefined>(undefined)
  const [slot, setSlot] = useState('')
  const [bannerableType, setBannerableType] =
    useState<BannerableTypeOption | null>(null)
  const [bannerableId, setBannerableId] = useState<number[]>([])

  useEffect(() => {
    return () => {
      setSlot('')
      setBannerableType(null)
      setBannerableId([])
    }
  }, [])

  useEffect(() => {
    if (banner) {
      setId(banner.id)
      setSlot(banner.slot)
      setBannerableType(
        BANNERABLE_TYPES.find((type) => type.value === banner.type) || null
      )
      if (banner.custom_banners) {
        setBannerableId(banner.custom_banners.map((b) => b.id))
      } else {
        setBannerableId(
          banner.bannerable_id != null ? [banner.bannerable_id] : []
        )
      }
    }
  }, [banner])

  const getOptions = useCallback(
    (arr: CustomBanner[] | null | undefined): BannerOption[] => {
      const result: BannerOption[] = []
      if (!arr) return result
      if (arr.length === 0) return result
      arr.forEach((elem) => {
        const obj: BannerOption = {
          value: String(elem.id),
          label: `${elem.id} ${elem.destination ?? ''}`,
        }
        result.push(obj)
      })
      return result
    },
    []
  )

  const getValue = useCallback(
    (
      bannerIds: number[],
      options: BannerOption[]
    ): MultiValue<BannerOption> | undefined => {
      if (!bannerIds || !options) return undefined
      return options.filter((option) =>
        bannerIds.includes(Number(option.value))
      )
    },
    []
  )

  const isValid = useCallback(() => {
    return slot && bannerableType
  }, [slot, bannerableType])

  const handleSave = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (isValid() && id !== undefined) {
        onUpdate(id, {
          slot,
          bannerable_type: bannerableType!.value, // isValid() checks it
          bannerable_id: bannerableId,
        })
      }
    },
    [isValid, onUpdate, id, slot, bannerableType, bannerableId]
  )

  const options = getOptions(customBanners)

  return (
    <form className='my-10 flex min-h-screen flex-col gap-4'>
      <div>
        <label className='block text-sm font-medium text-gray-700'>Slot:</label>
        <Input
          type='text'
          value={slot}
          onChange={(event) => setSlot(event.target.value)}
          className='h-8 w-37.5 lg:w-62.5'
        />
      </div>
      <div>
        <label className='block text-sm font-medium text-gray-700'>Tipo:</label>
        <Select
          options={BANNERABLE_TYPES}
          value={bannerableType}
          onChange={(
            selectedOption: SingleValue<BannerableTypeOption> | null
          ) => {
            setBannerableType(selectedOption)
            setBannerableId([])
          }}
          className='h-8 w-37.5 lg:w-62.5'
          classNamePrefix='select'
        />
      </div>
      <div>
        <label className='block text-sm font-medium text-gray-700'>
          Banner:
        </label>
        <Select
          isMulti
          name='selectedCategories'
          options={options}
          value={getValue(bannerableId, options)}
          className='h-8 w-full'
          classNamePrefix='select'
          placeholder='Seleziona i Banner'
          onChange={(selectedOptions: MultiValue<BannerOption> | null) => {
            setBannerableId(
              selectedOptions
                ? selectedOptions.map((option) => Number(option.value))
                : []
            )
          }}
        />
      </div>
      <div className='mt-10'>
        <Button disabled={!isValid()} onClick={handleSave}>
          Salva
        </Button>
      </div>
    </form>
  )
}
